import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { ActiveAnnouncement } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/**
 * Fetches the active announcements for the viewer (flag on/GA'd, unexpired,
 * unseen) and exposes the first-class trigger that records a view.
 *
 * `trigger(id)` is the "actually shown" moment — it POSTs the view; merely
 * reading the list has no side effect.
 */
export function useAnnouncements(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['announcements', 'active'],
    enabled,
    queryFn: async (): Promise<ActiveAnnouncement[]> => {
      const res = await fetch(`${API_URL}/announcements/active`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch announcements');
      const data = (await res.json()) as { announcements?: ActiveAnnouncement[] };
      return data.announcements ?? [];
    },
  });

  const triggerMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/announcements/${encodeURIComponent(id)}/view`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to record announcement view');
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['announcements', 'active'] }).catch(() => {});
  }, [queryClient]);

  return {
    announcements: query.data ?? [],
    isLoading: query.isLoading,
    trigger: triggerMutation.mutateAsync,
    invalidate,
  };
}
